import { Box, HStack, Text, VStack } from 'native-base';
import React from 'react';

import AddReservationToWallet from 'app/components/AddReservationToWallet';
import Ticket, { TicketProps } from 'app/components/TicketCarousel/Ticket';
import { FONT_SIZE } from 'app/components/TicketCarousel/Ticket/constants';
import TicketField from 'app/components/TicketCarousel/TicketField';
import { Locale } from 'app/generated/hygraph';
import { useI18n } from 'app/providers/I18nProvider';
import {
  EntitlementReservation,
  ReservationType,
  TicketCategory,
} from 'app/services/GuestCenterService.types';
import { showReservationTime, time } from 'app/services/ReservationHelper';

type ReservationTicketProps = TicketProps & {
  reservation: EntitlementReservation;
  reservationDisplay: {
    attractionName: string;
    dateTime: string;
    preArrivalTime?: string;
    preArrivalLabel?: string;
    departLabel?: string;
    timeNote?: string;
    option?: string;
    referenceNumber?: string;
    type?: ReservationType;
    hideReferenceNumber?: boolean;
    ticketCategory?: TicketCategory;
  };
  passBarcode: string;
  showAddToWallet?: boolean;
};

export default function ReservationTicket({
  passBarcode,
  reservation,
  reservationDisplay: {
    attractionName,
    option,
    referenceNumber,
    dateTime,
    preArrivalTime,
    preArrivalLabel,
    departLabel,
    timeNote,
    ticketCategory,
    hideReferenceNumber,
  },
  showAddToWallet = true,
  ...props
}: ReservationTicketProps) {
  const { t } = useI18n();

  const defaultLocale = Locale.En;
  const shouldShowReservationTime = showReservationTime(reservation, ticketCategory);
  const shouldShowTimeNote = !!timeNote?.trim();
  const { ianaTimezone } = reservation.attractionConfig;

  const dateField = (
    <TicketField
      label={t('app_reservation_date', { locale: defaultLocale })}
      value={time(dateTime, ianaTimezone).format('dddd, MMM. D, YYYY')}
    />
  );
  const timeNoteField = shouldShowTimeNote && (
    <Text bold lineHeight={FONT_SIZE}>
      {timeNote?.trim()}
    </Text>
  );

  return (
    <Ticket {...props}>
      <Box borderColor="gray.800" borderWidth="1" rounded="md">
        <Box bg="gray.800" py="0.5" roundedTop="sm">
          <Text color="white" fontSize={FONT_SIZE} textAlign="center" textTransform="uppercase">
            {t('app_reservation_title', { locale: defaultLocale })}
          </Text>
        </Box>

        <VStack p="3" space="2">
          <Box>
            <Text bold lineHeight={FONT_SIZE}>
              {attractionName}
            </Text>
            {!!option && (
              <Text fontSize={FONT_SIZE} lineHeight="sm">
                {option}
              </Text>
            )}
          </Box>
          {!hideReferenceNumber && !!referenceNumber && (
            <TicketField
              label={t('app_reservation_reference_number', { locale: defaultLocale })}
              selectable
              value={referenceNumber}
            />
          )}
          {preArrivalTime && preArrivalLabel && departLabel && shouldShowReservationTime ? (
            <VStack space="2">
              {dateField}
              <HStack justifyContent="space-between">
                <TicketField
                  label={preArrivalLabel}
                  value={time(preArrivalTime, ianaTimezone).format('h:mmA z')}
                />
                <TicketField
                  label={departLabel}
                  value={time(dateTime, ianaTimezone).format('h:mmA z')}
                />
              </HStack>
              {timeNoteField}
            </VStack>
          ) : (
            <HStack justifyContent="space-between">
              {dateField}

              {(shouldShowReservationTime || shouldShowTimeNote) && (
                <VStack space="0">
                  {shouldShowReservationTime && (
                    <TicketField
                      label={t('app_reservation_time', { locale: defaultLocale })}
                      value={time(dateTime, ianaTimezone).format('h:mmA z')}
                    />
                  )}
                  {timeNoteField}
                </VStack>
              )}
            </HStack>
          )}
        </VStack>
      </Box>

      {showAddToWallet && (
        <AddReservationToWallet
          passBarcode={passBarcode}
          reservation={reservation}
          ticketCategory={props.ticketCategory}
        />
      )}
    </Ticket>
  );
}
